<template>
    <admin-template>
        <div class="space-y-6">

            <!-- Header -->
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-2xl font-black tracking-tight text-black">Commerciaux</h1>
                    <p class="text-sm text-gray-400 mt-0.5">Gestion de l'équipe commerciale</p>
                </div>
                <button @click="openCreateModal" class="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold shadow-sm hover:shadow-md transition-all">
                    <i class="fas fa-plus"></i> Nouveau commercial
                </button>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div v-for="i in 3" :key="i" class="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse"></div>
            </div>

            <!-- Cards commerciaux -->
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div v-for="c in commerciaux" :key="c.id" class="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden">

                    <div class="p-5">
                        <!-- Avatar + actions -->
                        <div class="flex items-start justify-between mb-4">
                            <div class="flex items-center gap-3">
                                <div class="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-sm" :style="{ background: avatarColor(c.nom) }">
                                    {{ initials(c.nom) }}
                                </div>
                                <div>
                                    <div class="font-black text-black text-base">{{ c.nom }}</div>
                                    <div class="text-xs text-gray-400">{{ c.email }}</div>
                                </div>
                            </div>
                            <button @click="openEditModal(c)" class="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0">
                                <i class="fas fa-pen text-gray-500 text-xs"></i>
                            </button>
                        </div>

                        <!-- Taux -->
                        <div class="flex gap-2 mb-4">
                            <div class="flex-1 bg-orange-50 rounded-xl p-3 text-center">
                                <div class="text-xs text-orange-400 font-bold uppercase tracking-wider mb-0.5">1er mois</div>
                                <div class="text-xl font-black text-orange-500">{{ c.pourcentage_m1 }}%</div>
                                <div class="text-xs text-orange-400">= {{ (30 * c.pourcentage_m1 / 100).toFixed(0) }}€ / abo 30€</div>
                            </div>
                            <div class="flex-1 bg-blue-50 rounded-xl p-3 text-center">
                                <div class="text-xs text-blue-400 font-bold uppercase tracking-wider mb-0.5">Récurrent</div>
                                <div class="text-xl font-black text-blue-500">{{ c.pourcentage_year }}%</div>
                                <div class="text-xs text-blue-400">= {{ (30 * c.pourcentage_year / 100).toFixed(0) }}€/mois</div>
                            </div>
                        </div>

                        <!-- Stats -->
                        <div class="grid grid-cols-3 gap-2 mb-4">
                            <div class="text-center p-2 bg-gray-50 rounded-xl">
                                <div class="text-base font-black text-black">{{ c.nb_clients }}</div>
                                <div class="text-[10px] text-gray-400">clients</div>
                            </div>
                            <div class="text-center p-2 bg-gray-50 rounded-xl">
                                <div class="text-base font-black text-orange-500">{{ c.mrr_actuel | eur }}</div>
                                <div class="text-[10px] text-gray-400">comm./mois</div>
                            </div>
                            <div class="text-center p-2 rounded-xl" :class="c.total_du > 0 ? 'bg-orange-50' : 'bg-green-50'">
                                <div class="text-base font-black" :class="c.total_du > 0 ? 'text-orange-500' : 'text-green-500'">{{ c.total_du | eur }}</div>
                                <div class="text-[10px]" :class="c.total_du > 0 ? 'text-orange-400' : 'text-green-400'">à verser</div>
                            </div>
                        </div>

                        <button @click="openAttachModal(c)" class="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-gray-400 text-sm font-bold hover:border-orange-300 hover:text-orange-500 transition-all flex items-center justify-center gap-2">
                            <i class="fas fa-user-plus"></i> Rattacher un client
                        </button>
                    </div>
                </div>

                <!-- Card ajouter -->
                <div @click="openCreateModal" class="bg-white rounded-2xl border-2 border-dashed border-gray-200 shadow-sm hover:border-orange-300 hover:shadow-md transition-all cursor-pointer flex items-center justify-center min-h-[200px]">
                    <div class="text-center text-gray-300 hover:text-orange-400 transition-colors">
                        <div class="w-12 h-12 rounded-full border-2 border-dashed border-current flex items-center justify-center mx-auto mb-2">
                            <i class="fas fa-plus text-lg"></i>
                        </div>
                        <div class="text-sm font-bold">Nouveau commercial</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ═══════════════════════════════════════════════════════
             MODAL — CRÉER / MODIFIER UN COMMERCIAL
        ════════════════════════════════════════════════════════ -->
        <transition name="modal">
            <div v-if="formModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeModal"></div>
                <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">

                    <div class="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                        <h3 class="font-black text-black text-base">{{ editingId ? 'Modifier le commercial' : 'Nouveau commercial' }}</h3>
                        <button @click="closeModal" class="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                            <i class="fas fa-times text-gray-500"></i>
                        </button>
                    </div>

                    <div class="p-6 space-y-5">
                        <!-- Infos -->
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Nom complet</label>
                                <input v-model="form.nom" class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all" placeholder="Jean Dupont" />
                            </div>
                            <div>
                                <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                                <input v-model="form.email" type="email" class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all" placeholder="jean@email.com" />
                            </div>
                        </div>

                        <!-- Taux -->
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Commission 1er mois</label>
                                <div class="relative">
                                    <input v-model.number="form.pourcentage_m1" type="number" min="0" max="100" class="w-full px-4 py-2.5 pr-8 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all" />
                                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                                </div>
                            </div>
                            <div>
                                <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Commission récurrente</label>
                                <div class="relative">
                                    <input v-model.number="form.pourcentage_year" type="number" min="0" max="100" class="w-full px-4 py-2.5 pr-8 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all" />
                                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                                </div>
                            </div>
                        </div>

                        <!-- ─── SIMULATEUR ────────────────────────────────── -->
                        <div class="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                            <div class="px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-2">
                                <i class="fas fa-calculator text-orange-500 text-sm"></i>
                                <span class="text-sm font-black text-black">Simulateur de gains</span>
                            </div>

                            <div class="p-4 space-y-4">
                                <!-- Inputs sim -->
                                <div class="grid grid-cols-2 gap-3">
                                    <div>
                                        <label class="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Abonnement client</label>
                                        <div class="relative">
                                            <input v-model.number="sim.abo" type="number" min="0" class="w-full px-3 py-2 pr-7 rounded-xl border border-gray-200 focus:border-orange-400 outline-none text-sm bg-white" />
                                            <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
                                        </div>
                                    </div>
                                    <div>
                                        <label class="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Clients / mois</label>
                                        <div class="relative">
                                            <input v-model.number="sim.clients" type="number" min="0" class="w-full px-3 py-2 pr-12 rounded-xl border border-gray-200 focus:border-orange-400 outline-none text-sm bg-white" />
                                            <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">clients</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Résultats lisibles -->
                                <div class="space-y-2">
                                    <!-- Ce mois-ci -->
                                    <div class="bg-white rounded-xl p-3 border border-gray-100">
                                        <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Ce mois-ci</div>
                                        <div class="flex justify-between items-center mb-1.5">
                                            <span class="text-sm text-gray-600">Il gagne ({{ sim.clients }} × {{ sim.abo }}€ × {{ form.pourcentage_m1 }}%)</span>
                                            <span class="text-sm font-black text-orange-500">{{ simM1 | eur }}</span>
                                        </div>
                                        <div class="flex justify-between items-center">
                                            <span class="text-sm text-gray-600">Vous gardez ({{ sim.clients }} × {{ sim.abo }}€ - commission)</span>
                                            <span class="text-sm font-black text-green-600">{{ simVotreRevenuM1 | eur }}</span>
                                        </div>
                                    </div>

                                    <!-- Récurrent (à partir du mois suivant) -->
                                    <div class="bg-white rounded-xl p-3 border border-gray-100">
                                        <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Chaque mois suivant (récurrent sur ce lot)</div>
                                        <div class="flex justify-between items-center mb-1.5">
                                            <span class="text-sm text-gray-600">Il gagne ({{ sim.clients }} × {{ sim.abo }}€ × {{ form.pourcentage_year }}%)</span>
                                            <span class="text-sm font-black text-blue-500">{{ simRec | eur }}</span>
                                        </div>
                                        <div class="flex justify-between items-center">
                                            <span class="text-sm text-gray-600">Vous gardez</span>
                                            <span class="text-sm font-black text-green-600">{{ simVotreRevenuRec | eur }}</span>
                                        </div>
                                    </div>

                                    <!-- Total 12 mois -->
                                    <div class="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-3 border border-orange-100">
                                        <div class="text-xs font-bold text-orange-400 uppercase tracking-wider mb-2">Sur 12 mois (ce lot de {{ sim.clients }} clients)</div>
                                        <div class="flex justify-between items-center mb-1.5">
                                            <span class="text-sm font-bold text-gray-700">Il gagne au total</span>
                                            <span class="text-base font-black text-orange-500">{{ simTotal12 | eur }}</span>
                                        </div>
                                        <div class="flex justify-between items-center">
                                            <span class="text-sm font-bold text-gray-700">Vous gagnez au total</span>
                                            <span class="text-base font-black text-green-600">{{ simVotreTotal12 | eur }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Mini bar chart projection -->
                                <div v-if="sim.clients > 0">
                                    <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                        Sa commission mensuelle si il signe {{ sim.clients }} clients/mois pendant 12 mois
                                    </div>
                                    <div class="flex items-end gap-1 h-10 bg-white rounded-xl border border-gray-100 px-3 py-2">
                                        <div v-for="(v, i) in simProjection" :key="i"
                                            class="flex-1 rounded-sm transition-all duration-300"
                                            :class="i === simProjection.length - 1 ? 'bg-gradient-to-t from-red-500 to-orange-400' : 'bg-orange-200'"
                                            :style="{ height: (v / simProjection[11] * 100) + '%', minHeight: '3px' }"
                                            :title="'M' + (i+1) + ' : ' + formatEur(v)">
                                        </div>
                                    </div>
                                    <div class="flex justify-between text-xs text-gray-400 mt-1 px-1">
                                        <span>M1 : <strong class="text-orange-500">{{ simProjection[0] | eur }}</strong></span>
                                        <span>M6 : <strong class="text-blue-500">{{ simProjection[5] | eur }}</strong></span>
                                        <span>M12 : <strong class="text-green-600">{{ simProjection[11] | eur }}</strong></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="px-6 pb-6 flex justify-end gap-3">
                        <button @click="closeModal" class="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold hover:bg-gray-200 transition-colors">Annuler</button>
                        <button @click="saveCommercial" :disabled="formLoading" class="px-5 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold hover:shadow-md transition-all disabled:opacity-50 flex items-center gap-2">
                            <i class="fas fa-save"></i>
                            {{ formLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Créer') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ═══════════════════════════════════════════════════════
             MODAL — RATTACHER UN CLIENT
        ════════════════════════════════════════════════════════ -->
        <transition name="modal">
            <div v-if="attachModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="attachModal = false"></div>
                <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                    <div class="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                        <div>
                            <h3 class="font-black text-black text-base">Rattacher un client</h3>
                            <p class="text-xs text-gray-400" v-if="attachTarget">Pour {{ attachTarget.nom }}</p>
                        </div>
                        <button @click="attachModal = false" class="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                            <i class="fas fa-times text-gray-500"></i>
                        </button>
                    </div>
                    <div class="p-6 space-y-4">
                        <div>
                            <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">ID Client Termoflow</label>
                            <input v-model.number="attachForm.client_id" type="number" class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all" placeholder="123" />
                        </div>
                        <div>
                            <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Montant abonnement mensuel</label>
                            <div class="relative">
                                <input v-model.number="attachForm.abonnement_mensuel" type="number" class="w-full px-4 py-2.5 pr-16 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all" placeholder="30" />
                                <span class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">€/mois</span>
                            </div>
                        </div>
                        <div>
                            <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Date de signature</label>
                            <input v-model="attachForm.date_signature" type="date" class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm transition-all" />
                        </div>

                        <!-- Aperçu -->
                        <div v-if="attachTarget && attachForm.abonnement_mensuel" class="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-100">
                            <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Commissions générées sur 12 mois</div>
                            <div class="flex justify-between text-sm">
                                <span class="text-gray-500">Mois 1 ({{ attachTarget.pourcentage_m1 }}%)</span>
                                <strong class="text-orange-500">{{ (attachForm.abonnement_mensuel * attachTarget.pourcentage_m1 / 100).toFixed(2) }} €</strong>
                            </div>
                            <div class="flex justify-between text-sm">
                                <span class="text-gray-500">× 11 mois suivants ({{ attachTarget.pourcentage_year }}%)</span>
                                <strong class="text-blue-500">{{ (attachForm.abonnement_mensuel * attachTarget.pourcentage_year / 100 * 11).toFixed(2) }} €</strong>
                            </div>
                            <div class="flex justify-between text-sm pt-2 border-t border-gray-200">
                                <strong class="text-gray-700">Total</strong>
                                <strong class="text-green-600">{{ attachTotalPreview }} €</strong>
                            </div>
                        </div>
                    </div>
                    <div class="px-6 pb-6 flex justify-end gap-3">
                        <button @click="attachModal = false" class="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold hover:bg-gray-200 transition-colors">Annuler</button>
                        <button @click="attachClient" :disabled="attachLoading" class="px-5 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold hover:shadow-md transition-all disabled:opacity-50 flex items-center gap-2">
                            <i class="fas fa-link"></i>
                            {{ attachLoading ? 'Traitement...' : 'Rattacher & générer 12 mois' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="toast">
            <div v-if="toast.show" class="fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-sm font-bold"
                :class="toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'">
                <i :class="toast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                {{ toast.message }}
            </div>
        </transition>

    </admin-template>
</template>

<script>

export default {
    name: 'AdminCommerciauxListe',

    filters: {
        eur(v) {
            if (v === null || v === undefined) return '—';
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
        },
    },

    data() {
        return {
            loading: true,
            commerciaux: [],

            // Form
            formModal: false,
            formLoading: false,
            editingId: null,
            form: { nom: '', email: '', pourcentage_m1: 50, pourcentage_year: 20 },

            // Simulateur
            sim: { abo: 30, clients: 10 },

            // Attach
            attachModal: false,
            attachLoading: false,
            attachTarget: null,
            attachForm: { client_id: '', abonnement_mensuel: 30, date_signature: new Date().toISOString().slice(0, 10) },

            // Toast
            toast: { show: false, message: '', type: 'success' },
        };
    },

    computed: {
        // ── Simulateur ──────────────────────────────────────────
        simM1()             { return this.sim.clients * this.sim.abo * this.form.pourcentage_m1 / 100; },
        simRec()            { return this.sim.clients * this.sim.abo * this.form.pourcentage_year / 100; },
        simVotreRevenuM1()  { return this.sim.clients * this.sim.abo - this.simM1; },
        simVotreRevenuRec() { return this.sim.clients * this.sim.abo - this.simRec; },
        simTotal12()        { return this.simM1 + this.simRec * 11; },
        simVotreTotal12()   { return (this.sim.clients * this.sim.abo * 12) - this.simTotal12; },
        simProjection() {
            const { abo, clients } = this.sim;
            const pm1 = this.form.pourcentage_m1 / 100;
            const prec = this.form.pourcentage_year / 100;
            return Array.from({ length: 12 }, (_, m) =>
                +((clients * abo * pm1) + (m * clients * abo * prec)).toFixed(2)
            );
        },

        // Attach preview
        attachTotalPreview() {
            if (!this.attachTarget || !this.attachForm.abonnement_mensuel) return '0.00';
            const m1 = this.attachForm.abonnement_mensuel * this.attachTarget.pourcentage_m1 / 100;
            const rec = this.attachForm.abonnement_mensuel * this.attachTarget.pourcentage_year / 100;
            return (m1 + rec * 11).toFixed(2);
        },
    },

    mounted() {
        this.fetchCommerciaux();
    },

    methods: {
        async fetchCommerciaux() {
            this.loading = true;
            const r = await this.$request.get('/admin/commerciaux');
            this.commerciaux = r || [];
            this.loading = false;
        },

        openCreateModal() {
            this.form = { nom: '', email: '', pourcentage_m1: 50, pourcentage_year: 20 };
            this.editingId = null;
            this.formModal = true;
        },

        openEditModal(c) {
            this.form = { nom: c.nom, email: c.email, pourcentage_m1: c.pourcentage_m1, pourcentage_year: c.pourcentage_year };
            this.editingId = c.id;
            this.formModal = true;
        },

        closeModal() { this.formModal = false; this.formLoading = false; },

        async saveCommercial() {
            if (!this.form.nom || !this.form.email) { this.showToast('Nom et email obligatoires', 'error'); return; }
            this.formLoading = true;
            try {
                if (this.editingId) {
                    await this.$request.patch(`/admin/commerciaux/${this.editingId}`, this.form);
                    this.showToast('Commercial modifié', 'success');
                } else {
                    await this.$request.post('/admin/commerciaux', this.form);
                    this.showToast('Commercial créé', 'success');
                }
                await this.fetchCommerciaux();
                this.closeModal();
            } catch { this.showToast('Erreur lors de l\'enregistrement', 'error'); }
            this.formLoading = false;
        },

        openAttachModal(c) {
            this.attachTarget = c;
            this.attachForm = { client_id: '', abonnement_mensuel: 30, date_signature: new Date().toISOString().slice(0, 10) };
            this.attachModal = true;
        },

        async attachClient() {
            if (!this.attachForm.client_id) { this.showToast('ID client obligatoire', 'error'); return; }
            this.attachLoading = true;
            try {
                await this.$request.post(`/admin/commerciaux/${this.attachTarget.id}/clients`, this.attachForm);
                this.showToast('Client rattaché, 12 mois de commissions générés', 'success');
                await this.fetchCommerciaux();
                this.attachModal = false;
            } catch { this.showToast('Erreur lors du rattachement', 'error'); }
            this.attachLoading = false;
        },

        initials(n) { return (n || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); },
        avatarColor(n) {
            const colors = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#F59E0B'];
            let h = 0; for (let i = 0; i < (n || '').length; i++) h = n.charCodeAt(i) + ((h << 5) - h);
            return colors[Math.abs(h) % colors.length];
        },
        formatEur(v) {
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
        },
        showToast(message, type = 'success') {
            this.toast = { show: true, message, type };
            setTimeout(() => { this.toast.show = false; }, 4000);
        },
    },
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter, .modal-leave-to { opacity: 0; }
.toast-enter-active, .toast-leave-active { transition: all 0.3s ease; }
.toast-enter, .toast-leave-to { opacity: 0; transform: translateY(12px); }
</style>